import { isFunction, snakeCase } from "lodash";
import { useCallback } from "react";
import produce from "immer";
import { getOrElse } from "../_helpers/_utilities";

/**
 * @return {{event: function(action: string, label: string, topic: string, payload: object, rawPayload: boolean), pageView: function(taxonomy: string, topic: string, payload: object?, rawPayload: boolean?), virtualPageView: function(pseudoPath: string, taxonomy: string, topic: string, payload: object, rawPayload: boolean)}}
 */
const useAnonymousDataLayer = (createPayloadCallback = null) => {
	const path = typeof window !== "undefined" ? window.location.pathname : "";

	const createPayload = useCallback(
		(data) => {
			if (!isFunction(createPayloadCallback)) {
				return {
					...{
						event: "page_view",
						...data,
					},
				};
			}

			return createPayloadCallback(data);
		},
		[createPayloadCallback]
	);
	const push = useCallback(
		(data, rawPayload = false) => {
			window.dataLayer = typeof window !== "undefined" ? window.dataLayer || [] : [];
			window.dataLayer.push(
				createPayload(
					rawPayload
						? data
						: produce(data, (draft) => {
							for (let prop in draft) {
								const formattedVal = draft[prop] ? snakeCase(draft[prop].toString().toLowerCase()) : null;
								delete draft[prop];
								draft[prop.toLowerCase()] = formattedVal;
							}
						})
				)
			);
		},
		[createPayload]
	);

	/**
	 * Pushes a page view event to the Data Layer
	 * @param {string} taxonomy Top-level taxonomy description of the page (e.g "Career Hub") - page_category
	 * @param {string} topic More granular descrioption of page (e.g "Career Hub Problem Space") - page_type
	 * @param {object} payload
	 * @param {boolean} rawPayload Do not alter the object of `payload` if this is true
	 * @param {string|null} overridePath Override path of this pageview - page_name
	 */
	const pageView = useCallback(
		(taxonomy, topic, payload = {}, rawPayload = false, overridePath = null) => {
			push(
				{
					event: "pageview",
					page_type: snakeCase(topic.toLowerCase()),
					page_name: overridePath || snakeCase(path.toLowerCase()),
					page_category: snakeCase(taxonomy.toLowerCase()),
					...payload,
				},
				rawPayload
			);
		},
		[path, push]
	);

	/**
	 * Pushes a virtual page view event to the Data Layer
	 * @param {string} pseudoPath URL of virtual page (e.g "/occupations/345/Analyst")
	 * @param {string} taxonomy Top-level taxonomy description of the page (e.g "Occupations")
	 * @param {string} topic More granular descrioption of page (e.g "Occupation Preview")
	 * @param {object} payload
	 * @param {boolean} rawPayload Do not alter the object of `payload` if this is true
	 */
	const virtualPageView = useCallback(
		(pseudoPath, taxonomy, topic, payload = {}, rawPayload = false) => {
			push(
				{
					event: "virtual_pageview",
					page_type: snakeCase(topic.toLowerCase()),
					page_name: snakeCase(pseudoPath.toLowerCase()),
					page_category: snakeCase(taxonomy.toLowerCase()),
					...payload,
				},
				rawPayload
			);
		},
		[push]
	);

	/**
	 * Pushes a custom event trigger to the Data Layer
	 * @param {string} action Grandular description of the action performed (e.g "Filter Topic")
	 * @param {string} label Top-level taxonomy description of the page (e.g "Cover Letters")
	 * @param {string} topic More granular descrioption of group of interactions (e.g "Career Hub Interactions")
	 * @param {object} payload
	 * @param {boolean} rawPayload Do not alter the object of `payload` if this is true
	 */
	const event = useCallback(
		(action, label, topic, payload = {}, rawPayload = false) => {
			push(
				{
					event: "custom_event",
					event_action: getOrElse(() => snakeCase(action.toLowerCase()), ""),
					event_category: getOrElse(() => snakeCase(topic.toLowerCase()), ""),
					event_label: getOrElse(() => snakeCase(label.toLowerCase()), ""),
					...payload,
				},
				rawPayload
			);
		},
		[push]
	);

	return {
		pageView,
		virtualPageView,
		event,
	};
};

export default useAnonymousDataLayer;
